import React, { useEffect, useMemo, useState } from "react";
import { dateLocale } from "@/lib/dateTime";
import { Link } from "react-router-dom";
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Loader2, Minus, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, initialsOf } from "@/components/patterns";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api, unwrapObject } from "@/lib/api";
import { fmtMoney, fmtNum } from "@/components/reports/ReportWidgets";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { cn } from "@/lib/utils";

interface TargetRow {
  user_id: string;
  name: string;
  role_code: string;
  role_name: string;
  sales_head: string | null;
  target_amount: number;
  target_conversions: number;
  target_connected_calls: number;
  target_talk_hours: number;
  connected_calls: number;
  talk_hours: number;
  target_daily_calls: number;
  calls_per_day: number;
  won_amount: number;
  leads_converted: number;
  achieved_pct: number | null;
  can_edit: boolean;
}
interface GapDeal {
  id: string;
  name: string;
  amount: number;
  probability: number;
  stage_name: string | null;
  owner_name: string | null;
  expected_close_date: string;
}
interface TargetData {
  period: string;
  quarter: boolean;
  from: string;
  to: string;
  rows: TargetRow[];
  summary: {
    target_amount: number;
    won_amount: number;
    target_conversions: number;
    leads_converted: number;
    target_connected_calls: number;
    connected_calls: number;
    target_talk_hours: number;
    talk_hours: number;
    target_daily_calls: number;
    calls_per_day: number;
    committed_amount: number;
    elapsed_pct: number;
    days_left: number;
  };
  gap_deals: GapDeal[];
}

type Mode = "month" | "quarter";
type Measure = "revenue" | "leads" | "calls" | "talk" | "daily";
type Field = "target_amount" | "target_conversions" | "target_connected_calls" | "target_talk_hours" | "target_daily_calls";
const FIELDS: Field[] = ["target_amount", "target_conversions", "target_connected_calls", "target_talk_hours", "target_daily_calls"];
type Status = "none" | "ahead" | "on" | "behind" | "risk" | "done";

const periodOf = (mode: Mode, d: Date) => (mode === "month" ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`);
const titleOf = (mode: Mode, d: Date) => {
  if (mode === "month") return d.toLocaleDateString(dateLocale(), { month: "long", year: "numeric" });
  const first = Math.floor(d.getMonth() / 3) * 3;
  const a = new Date(d.getFullYear(), first, 1).toLocaleDateString(dateLocale(), { month: "short" });
  const b = new Date(d.getFullYear(), first + 2, 1).toLocaleDateString(dateLocale(), { month: "short" });
  return `${a} – ${b} ${d.getFullYear()}`;
};
const shift = (mode: Mode, d: Date, by: number) => new Date(d.getFullYear(), d.getMonth() + by * (mode === "month" ? 1 : 3), 1);

/** Where a person stands against the share of the period that has gone by */
function statusOf(achieved: number, target: number, elapsed: number): Status {
  if (!target) return "none";
  const pct = (achieved / target) * 100;
  if (pct >= 100) return "done";
  if (pct >= elapsed + 10) return "ahead";
  if (pct >= elapsed - 10) return "on";
  if (pct >= elapsed - 30) return "behind";
  return "risk";
}
const STATUS: Record<Status, { label: string; tone: string; icon: typeof Check }> = {
  none: { label: "No target", tone: "bg-muted text-muted-foreground", icon: Minus },
  done: { label: "Target met", tone: "bg-success-soft text-success", icon: Check },
  ahead: { label: "Ahead", tone: "bg-success-soft text-success", icon: Check },
  on: { label: "On pace", tone: "bg-accent text-accent-foreground", icon: Minus },
  behind: { label: "Behind", tone: "bg-warning-soft text-warning", icon: AlertTriangle },
  risk: { label: "At risk", tone: "bg-danger-soft text-danger", icon: X },
};

/** A bar with a line where the person should be by today */
function Progress({ achieved, target, expected, committed = 0 }: { achieved: number; target: number; expected: number; committed?: number }) {
  const pct = target ? Math.min(100, (achieved / target) * 100) : 0;
  const soft = target ? Math.min(100 - pct, (committed / target) * 100) : 0;
  return (
    <div className="relative h-2 rounded-full bg-border/70" role="img" aria-label={`${Math.round(pct)}% of target, ${Math.round(expected)}% expected by today`}>
      <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${pct}%` }} />
      {soft > 0 && <div className="absolute inset-y-0 bg-primary/35" style={{ left: `${pct}%`, width: `${soft}%` }} />}
      {target > 0 && <div className="absolute -inset-y-1 w-0.5 bg-foreground" style={{ left: `${Math.min(100, expected)}%` }} />}
    </div>
  );
}

export function TargetsPage() {
  const { hasPermission, getScope } = useAuth();
  const { success, error } = useToast();
  const [mode, setMode] = useState<Mode>("month");
  const [anchor, setAnchor] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [measure, setMeasure] = useState<Measure>("revenue");
  const [data, setData] = useState<TargetData | null>(null);
  const [edits, setEdits] = useState<Record<string, Record<Field, string>>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const canSet = hasPermission("target.set");
  const isSuperAdmin = getScope("report") === "all";
  const period = periodOf(mode, anchor);

  const load = async () => {
    setLoading(true);
    try {
      setData(unwrapObject<TargetData>(await api.get(`/api/Target/List?period=${period}`)));
      setEdits({});
    } catch (err: any) {
      error("Targets", err.message || "Failed to load targets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    // Drop the old period's rows and unsaved edits straight away, so nothing typed there can be saved against the new one
    setData(null);
    setEdits({});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  const rows = data?.rows || [];
  const dirtyIds = Object.keys(edits);
  const valueOf = (r: TargetRow, f: Field) => edits[r.user_id]?.[f] ?? String(r[f] || "");
  const setField = (r: TargetRow, f: Field, value: string) => {
    if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    setEdits((prev) => ({
      ...prev,
      [r.user_id]: { ...(Object.fromEntries(FIELDS.map((k) => [k, prev[r.user_id]?.[k] ?? String(r[k] || "")])) as Record<Field, string>), [f]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/api/Target/Save", {
        period,
        items: dirtyIds.map((id) => ({
          user_id: id,
          target_amount: Number(edits[id]?.target_amount) || 0,
          target_conversions: Math.round(Number(edits[id]?.target_conversions) || 0),
          target_connected_calls: Math.round(Number(edits[id]?.target_connected_calls) || 0),
          target_talk_hours: Number(edits[id]?.target_talk_hours) || 0,
          target_daily_calls: Math.round(Number(edits[id]?.target_daily_calls) || 0),
        })),
      });
      success("Targets saved", `${dirtyIds.length} target${dirtyIds.length === 1 ? "" : "s"} for ${titleOf(mode, anchor)}`);
      await load();
    } catch (err: any) {
      error("Save failed", err.message || "Could not save targets");
    } finally {
      setSaving(false);
    }
  };

  const groups = useMemo(
    () =>
      [
        { title: "Sales heads", note: "Team target, compared with the whole team's result", rows: rows.filter((r) => r.role_code === "SALES_HEAD") },
        { title: "Sales executives", note: "Compared with each person's own result", rows: rows.filter((r) => r.role_code === "SALES_EXEC") },
      ].filter((g) => g.rows.length),
    [rows],
  );

  const revenue = measure === "revenue";
  const M = {
    revenue: { field: "target_amount" as Field, done: (r: TargetRow) => r.won_amount, sumTarget: (x: NonNullable<typeof data>["summary"]) => x.target_amount, sumDone: (x: NonNullable<typeof data>["summary"]) => x.won_amount, verb: "won", noun: "Revenue", fmt: (v: number) => fmtMoney(v) },
    leads: { field: "target_conversions" as Field, done: (r: TargetRow) => r.leads_converted, sumTarget: (x: NonNullable<typeof data>["summary"]) => x.target_conversions, sumDone: (x: NonNullable<typeof data>["summary"]) => x.leads_converted, verb: "converted", noun: "Conversion", fmt: (v: number) => fmtNum(v) },
    calls: { field: "target_connected_calls" as Field, done: (r: TargetRow) => r.connected_calls, sumTarget: (x: NonNullable<typeof data>["summary"]) => x.target_connected_calls, sumDone: (x: NonNullable<typeof data>["summary"]) => x.connected_calls, verb: "connected", noun: "Connected calls", fmt: (v: number) => fmtNum(v) },
    talk: { field: "target_talk_hours" as Field, done: (r: TargetRow) => r.talk_hours, sumTarget: (x: NonNullable<typeof data>["summary"]) => x.target_talk_hours, sumDone: (x: NonNullable<typeof data>["summary"]) => x.talk_hours, verb: "talked", noun: "Talk time", fmt: (v: number) => `${fmtNum(v)} h` },
    daily: { field: "target_daily_calls" as Field, done: (r: TargetRow) => r.calls_per_day, sumTarget: (x: NonNullable<typeof data>["summary"]) => x.target_daily_calls, sumDone: (x: NonNullable<typeof data>["summary"]) => x.calls_per_day, verb: "made a day", noun: "Calls a day", fmt: (v: number) => fmtNum(v) },
  }[measure];
  const daily = measure === "daily";
  const sm = data?.summary;
  const target = sm ? M.sumTarget(sm) : 0;
  const achieved = sm ? M.sumDone(sm) : 0;
  // Calls a day is a rate, not a running total, so the whole target is due every day
  const elapsed = daily ? 100 : sm?.elapsed_pct ?? 0;
  const pct = target ? Math.round((achieved / target) * 100) : 0;
  const teamStatus = sm ? statusOf(achieved, target, elapsed) : "none";
  const left = Math.max(0, target - achieved);
  const weeks = Math.max(1, (sm?.days_left || 0) / 7);
  const money = (v: number) => fmtMoney(v);
  const fmt = M.fmt;
  const perWeek = revenue ? money(left / weeks) : measure === "talk" ? `${(left / weeks).toFixed(1)} h` : (left / weeks).toFixed(left / weeks < 10 ? 1 : 0);

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHeader
        title="Targets"
        actions={
          <>
            {hasPermission("report.export") && <ExportMenu fileUrl={(format) => `/api/Report/Download/targets?format=${format}&period=${period}`} />}
            {canSet && mode === "month" && (
              <Button size="sm" onClick={save} disabled={saving || dirtyIds.length === 0}>
                {saving ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Save className="mr-1.5 size-4" />}
                Save{dirtyIds.length ? ` (${dirtyIds.length})` : ""}
              </Button>
            )}
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2.5">
        <h2 className="text-[19px] font-semibold tracking-tight">{titleOf(mode, anchor)}</h2>
        <Button variant="outline" size="icon-sm" aria-label="Earlier" onClick={() => setAnchor((d) => shift(mode, d, -1))}>
          <ChevronLeft className="size-4" />
        </Button>
        <Button variant="outline" size="icon-sm" aria-label="Later" onClick={() => setAnchor((d) => shift(mode, d, 1))}>
          <ChevronRight className="size-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setAnchor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>
          This {mode}
        </Button>
        <div className="flex-1" />
        <div role="group" aria-label="Period" className="flex overflow-hidden rounded-lg border border-border">
          {(["month", "quarter"] as Mode[]).map((m) => (
            <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)} className={cn("h-[30px] px-3 text-xs capitalize", mode === m ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground hover:text-foreground")}>
              {m}
            </button>
          ))}
        </div>
        <div role="tablist" aria-label="Measure" className="flex overflow-hidden rounded-lg border border-border">
          {([["revenue", "Revenue won"], ["leads", "Leads converted"], ["calls", "Connected calls"], ["talk", "Talk time"], ["daily", "Calls a day"]] as [Measure, string][]).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={measure === k} onClick={() => setMeasure(k)} className={cn("h-[30px] px-3 text-xs", measure === k ? "bg-sidebar font-semibold text-white" : "bg-card text-muted-foreground hover:text-foreground")}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {sm && (
            <section aria-label="Team progress" className="flex flex-wrap items-center gap-7 rounded-xl border border-border bg-card px-5 py-4">
              <div className="flex min-w-[210px] flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">{isSuperAdmin ? "Whole team" : canSet ? "Your team" : "You"} · {M.verb} so far</span>
                <span className="font-mono text-[32px] font-semibold leading-tight tracking-tight">{fmt(achieved)}</span>
                <span className="text-muted-foreground">
                  {target ? (
                    <>
                      of {fmt(target)} target · <strong className="text-foreground">{pct}%</strong>
                    </>
                  ) : (
                    "No target set for this period"
                  )}
                </span>
              </div>
              <div className="flex min-w-[260px] flex-1 flex-col gap-2">
                <Progress achieved={achieved} target={target} expected={elapsed} committed={revenue ? sm.committed_amount : 0} />
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-primary" />{M.verb[0]!.toUpperCase() + M.verb.slice(1)} {fmt(achieved)}</span>
                  {revenue && sm.committed_amount > 0 && <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-sm bg-primary/35" />Likely to close {money(sm.committed_amount)}</span>}
                  {daily ? (
                    <span>Average calls made per calling day so far</span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-0.5 bg-foreground" />Expected by today: {Math.round(elapsed)}%</span>
                  )}
                </div>
              </div>
              <div className="flex min-w-[180px] flex-col gap-0.5">
                {target > 0 && (
                  <span className={cn("inline-flex items-center gap-1.5 font-semibold", teamStatus === "risk" || teamStatus === "behind" ? "text-warning" : "text-success")}>
                    {React.createElement(STATUS[teamStatus].icon, { className: "size-3.5" })}
                    {STATUS[teamStatus].label}
                  </span>
                )}
                {target > 0 && left > 0 && elapsed < 100 && (
                  <span className="text-xs text-muted-foreground">
                    Need {fmt(left)} more in {sm.days_left} day{sm.days_left === 1 ? "" : "s"} · {perWeek} a week
                  </span>
                )}
                {target > 0 && left === 0 && <span className="text-xs text-muted-foreground">Target reached.</span>}
              </div>
            </section>
          )}

          {groups.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border py-12 text-center text-sm text-muted-foreground">There is nobody you can see targets for yet.</p>
          ) : (
            groups.map((g) => (
              <section key={g.title} className="overflow-x-auto rounded-lg border border-border bg-card">
                <div className="flex items-baseline justify-between gap-3 border-b border-border px-3.5 py-2.5">
                  <h3 className="text-sm font-semibold">{g.title}</h3>
                  <span className="text-xs text-muted-foreground">{g.note}</span>
                </div>
                <table className="w-full min-w-[820px] border-collapse text-[12.5px]">
                  <thead>
                    <tr className="bg-panel text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-3.5 py-2 font-semibold">Person</th>
                      <th className="px-2.5 py-2 text-right font-semibold">Target</th>
                      <th className="px-2.5 py-2 text-right font-semibold">{M.verb[0]!.toUpperCase() + M.verb.slice(1)}</th>
                      <th className="w-[260px] px-2.5 py-2 font-semibold">Progress · line = expected today</th>
                      <th className="px-2.5 py-2 text-right font-semibold">Still needed</th>
                      <th className="px-3.5 py-2 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => {
                      const field = M.field;
                      const t = Number(valueOf(r, field)) || 0;
                      const a = M.done(r);
                      const st = statusOf(a, t, elapsed);
                      const S = STATUS[st];
                      const p = t ? Math.round((a / t) * 100) : null;
                      return (
                        <tr key={r.user_id} className="border-t border-border/70">
                          <td className="px-3.5 py-2.5">
                            <span className="flex items-center gap-2.5 font-semibold">
                              <span className="flex size-[26px] items-center justify-center rounded-full bg-accent text-[10px] font-bold text-accent-foreground">{initialsOf(r.name)}</span>
                              <span className="flex flex-col leading-tight">
                                <Link to={`/reports/user/${r.user_id}`} className="hover:text-primary hover:underline">{r.name}</Link>
                                {g.title === "Sales executives" && r.sales_head && <span className="text-[11px] font-normal text-muted-foreground">{r.sales_head}</span>}
                              </span>
                            </span>
                          </td>
                          <td className="px-2.5 py-2.5 text-right">
                            {r.can_edit ? (
                              <Input
                                inputMode="decimal"
                                aria-label={`${M.noun} target for ${r.name}`}
                                className="ml-auto h-7 w-[120px] text-right font-mono text-xs"
                                value={valueOf(r, field)}
                                placeholder="0"
                                onChange={(e) => setField(r, field, revenue || measure === "talk" ? e.target.value : e.target.value.replace(/\D/g, ""))}
                              />
                            ) : (
                              <span className="font-mono">{t ? fmt(t) : "—"}</span>
                            )}
                          </td>
                          <td className="px-2.5 py-2.5 text-right font-mono font-semibold">{fmt(a)}</td>
                          <td className="px-2.5 py-2.5">
                            <span className="flex items-center gap-2">
                              <span className="flex-1">
                                <Progress achieved={a} target={t} expected={elapsed} />
                              </span>
                              <span className="w-10 text-right font-mono text-xs">{p === null ? "" : `${p}%`}</span>
                            </span>
                          </td>
                          <td className="px-2.5 py-2.5 text-right font-mono text-muted-foreground">{t ? (a >= t ? "—" : fmt(t - a)) : ""}</td>
                          <td className="px-3.5 py-2.5">
                            <span className={cn("inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-md px-2 text-[11.5px] font-semibold", S.tone)}>
                              <S.icon className="size-3" aria-hidden="true" />
                              {S.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </section>
            ))
          )}

          <div className="flex flex-wrap gap-3">
            <section className="flex min-w-[320px] flex-[1.3] flex-col gap-1 rounded-lg border border-border bg-card px-3.5 py-3">
              <h3 className="text-sm font-semibold">What could close the gap</h3>
              <span className="text-xs text-muted-foreground">Open deals expected to close in this period, biggest first</span>
              {(data?.gap_deals || []).length === 0 && <span className="pt-2 text-xs text-muted-foreground">No open deals have a close date in this period.</span>}
              {(data?.gap_deals || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 border-t border-border/70 py-1.5">
                  <Link to={`/deals/${d.id}`} className="min-w-0 truncate text-[13px] font-medium text-primary hover:underline">{d.name}</Link>
                  <span className="flex shrink-0 items-center gap-3 text-xs">
                    <span className="text-muted-foreground">{[d.stage_name, d.owner_name].filter(Boolean).join(" · ")}</span>
                    <span className="font-mono font-semibold">{money(d.amount)}</span>
                  </span>
                </div>
              ))}
            </section>
            <section className="flex min-w-[260px] flex-1 flex-col gap-2 rounded-lg border border-border bg-card px-3.5 py-3">
              <h3 className="text-sm font-semibold">How targets are counted</h3>
              <p className="text-[13px] text-foreground/80">A deal counts in the month its won date falls in, for the owner at the time it was won. Value is before GST.</p>
              <p className="text-[13px] text-foreground/80">A quarter adds up its three months. Targets are set one month at a time.</p>
              <p className="text-[13px] text-foreground/80">Calls a day is the calls to make on each calling day of the month. Achieved is the average over the calling days gone by.</p>
              <p className="text-[13px] text-foreground/80">Sales heads see their team. Sales executives see only their own row.</p>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
